// @flow

import ko from 'rgs-knockout';

import type { BindingLifecycleHooks } from '../core/contracts/ko-binding.flow';
import type { LodashFunctor } from './render';
import { unwrap } from './unwrap';
import { asyncCallFp } from './async';

export type applyBindingNodeParams = {
	model: *,
	intoHtml?: any;
	template: LodashFunctor | string;
}

const registerComponent = (selector: string, definition): void => {
	ko.components.register(selector, {
		viewModel: {
			createViewModel: definition.resolver,
		},
		template: definition.template,
	});
};


const registerBinding = (name: string, lifecycleHooks: BindingLifecycleHooks): void => {
	ko.bindingHandlers[name] = {
		init: lifecycleHooks.onInit,
		update: lifecycleHooks.onUpdates,
	};
};

const addDisposeCallback = (element: Node, callback: Function): void => {
	ko.utils.domNodeDisposal.addDisposeCallback(element, callback);
};

const registerBindingWithDispose = (name: string, lifecycleHooks: BindingLifecycleHooks): void => {
	let hasDisposeSubscription = false;

	const addDisposableHandler = (element: Node): void =>
		addDisposeCallback(element, asyncCallFp(() => lifecycleHooks.onDispose(element)));

	registerBinding(name, {
		onInit: (element: Node, ...args: *[]): * => {
			if (!hasDisposeSubscription) {
				addDisposableHandler(element);
				hasDisposeSubscription = true;
			}

			return lifecycleHooks.onInit(element, ...args);
		},
		onUpdates: lifecycleHooks.onUpdates,
	});
};

const applyBindingsToNode = (element: Node, params: applyBindingNodeParams): void => {
	ko.applyBindingsToNode(element, {
		template: {
			html: unwrap(params.template, params.intoHtml),
			data: params.model,
		},
	});
};

const withDisposable = (element: Node, callback: Function) =>
	ko.computed(callback, null, {disposeWhenNodeIsRemoved: element});

export {
	registerBindingWithDispose,
	applyBindingsToNode,
	addDisposeCallback,
	registerComponent,
	registerBinding,
	withDisposable,
};
